import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import './RelationshipGraph.css';

// Family-tree data + portraits ship as static assets in client/public so the
// Vercel build renders without a backend. Snapshot is regenerated by
// server/scripts/build-graph-snapshots.js.
const SNAPSHOT_BASE = process.env.PUBLIC_URL || '';
const FALLBACK_TREE_URL = `${SNAPSHOT_BASE}/relationship-graph/_family-tree.json`;
const freshSnapshotUrl = (url) => `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
const treeUrlForVideo = (videoId) => {
  const normalized = (videoId || '').replace(/\.[^.]+$/, '');
  if (!normalized || normalized === 'house_of_dragon_05') return FALLBACK_TREE_URL;
  return `${SNAPSHOT_BASE}/relationship-graph/_family-tree-${encodeURIComponent(normalized)}.json`;
};
// Backend endpoint: detailed spoiler-safe profile (LLM-composed, falls back to
// structured KB data when LLM is unreachable). Vercel build w/o REACT_APP_API_URL
// will simply 404 and the panel renders the static fallback only.
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Layout constants (svg "graph units" — pre-zoom). Tuned so 8-wide row 0 +
// 10-wide row 1 fit at 1× without overlap.
const COL_W = 150;
const ROW_H = 320;
const PORTRAIT_R = 38;
const COMPANION_R = 18;
const CANVAS_PAD = 100;
const CHILD_BUS_OFFSET = 110;   // vertical drop from parent row to the kid-bus bar
const SIBLING_BAR_OFFSET = -75; // sibling bar floats above the heads
const NAME_BLOCK_Y = PORTRAIT_R + 18;

// Conflict edge styling — color + dash by relation_kind. Tweak in CSS too.
const CONFLICT_STYLE = {
  enemy:  { className: 'rg-conf rg-conf-enemy' },
  ally:   { className: 'rg-conf rg-conf-ally' },
  secret: { className: 'rg-conf rg-conf-secret' },
  friend: { className: 'rg-conf rg-conf-friend' },
  blood:  { className: 'rg-conf rg-conf-blood' },
};

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 3;

// ─── Layout: convert characters[] into pixel coords ─────────────────
function computeLayout(tree) {
  const positions = {};
  const byGen = {};
  for (const c of tree.characters) {
    (byGen[c.generation] ||= []).push(c);
  }
  for (const gen of Object.keys(byGen)) {
    byGen[gen].sort((a, b) => a.lineage_x - b.lineage_x);
  }
  // Find global max lineage_x to compute canvas width
  let maxX = 0, maxGen = 0;
  for (const c of tree.characters) {
    if (c.lineage_x > maxX) maxX = c.lineage_x;
    if (c.generation > maxGen) maxGen = c.generation;
  }

  for (const c of tree.characters) {
    positions[c.character_id] = {
      x: CANVAS_PAD + c.lineage_x * COL_W + COL_W / 2,
      y: CANVAS_PAD + c.generation * ROW_H + ROW_H / 2,
    };
  }

  const width = CANVAS_PAD * 2 + (maxX + 1) * COL_W;
  const height = CANVAS_PAD * 2 + (maxGen + 1) * ROW_H;
  return { positions, width, height };
}

// Group parent_child + parent_couple by parent set so we can draw one bracket
// per group (parent → bus bar → multiple kids).
function groupParentChildren(kinEdges) {
  const groups = new Map();
  for (const e of kinEdges) {
    let key, parents;
    if (e.kind === 'parent_couple') {
      key = e.parents.slice().sort().join('+');
      parents = e.parents.slice().sort();
    } else if (e.kind === 'parent_child') {
      key = e.from;
      parents = [e.from];
    } else {
      continue;
    }
    if (!groups.has(key)) groups.set(key, { parents, children: [] });
    groups.get(key).children.push(e.to);
  }
  return [...groups.values()];
}

// ─── Pure rendering helpers ─────────────────────────────────────────
function PillLabel({ x, y, text, kind = 'kin' }) {
  // Width is approximate — chinese chars are ~14px wide at the font size used.
  const padX = 10;
  const w = text.length * 16 + padX * 2;
  const h = 22;
  return (
    <g transform={`translate(${x}, ${y})`} className={`rg-pill rg-pill-${kind}`}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={4} ry={4} />
      <text x={0} y={1} dominantBaseline="middle" textAnchor="middle">{text}</text>
    </g>
  );
}

function DragonGlyph({ size = 14 }) {
  const s = size / 22;
  return (
    <g transform={`scale(${s})`} className="rg-companion-glyph">
      <path d="M-6 -8 L-3.5 -10.5 M6 -8 L3.5 -10.5" />
      <path d="M-9 -3 C-10 -7 -6 -8 -3 -7 L3 -7 C6 -8 10 -7 9 -3 C10 0 8.5 4 5 6 L-5 6 C-8.5 4 -10 0 -9 -3 Z" />
      <circle cx="0" cy="-2" r="1.4" />
    </g>
  );
}

// Dragon attached to its rider — hangs below the name block.
function Companion({ data }) {
  if (!data) return null;
  const cy = NAME_BLOCK_Y + 32 + COMPANION_R;
  const clipId = `rg-comp-${data.character_id}`;
  return (
    <g className="rg-companion" transform={`translate(0, ${cy})`} aria-hidden>
      <defs>
        <clipPath id={clipId}>
          <circle cx={0} cy={0} r={COMPANION_R - 1} />
        </clipPath>
      </defs>
      <circle className="rg-companion-bg" r={COMPANION_R} />
      {data.portrait_url ? (
        <image
          href={data.portrait_url} xlinkHref={data.portrait_url}
          x={-(COMPANION_R - 1)} y={-(COMPANION_R - 1)}
          width={(COMPANION_R - 1) * 2} height={(COMPANION_R - 1) * 2}
          clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <DragonGlyph size={COMPANION_R * 1.4} />
      )}
      <text className="rg-companion-name" x={0} y={COMPANION_R + 11}>{data.display_name}</text>
    </g>
  );
}

function CharacterNode({ char, position, highlighted, focused, dead, dimmed, portraitUrl, onClick }) {
  const clipId = `rg-clip-${char.character_id}`;
  const cls = [
    'rg-node',
    highlighted ? 'is-highlighted' : '',
    focused ? 'is-focused' : '',
    dead ? 'is-dead' : '',
    dimmed ? 'is-dimmed' : '',
  ].filter(Boolean).join(' ');
  // portraitUrl override — used for time-driven actor swaps (young → adult).
  // Falls back to whatever the static family-tree.json baked in.
  const url = portraitUrl || char.portrait_url;
  return (
    <g
      className={cls}
      transform={`translate(${position.x}, ${position.y})`}
      onClick={(e) => { e.stopPropagation(); onClick(char.character_id); }}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx={0} cy={0} r={PORTRAIT_R - 2} />
        </clipPath>
      </defs>
      <circle className="rg-portrait-ring" r={PORTRAIT_R} />
      {url ? (
        <image
          href={url} xlinkHref={url}
          x={-(PORTRAIT_R - 2)} y={-(PORTRAIT_R - 2)}
          width={(PORTRAIT_R - 2) * 2} height={(PORTRAIT_R - 2) * 2}
          clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <text className="rg-initial" y={6}>{(char.display_name || '').slice(0, 1)}</text>
      )}

      {char.epithet && (
        <g className="rg-epithet" transform={`translate(0, ${-PORTRAIT_R - 4})`}>
          <rect x={-32} y={-10} width={64} height={20} rx={2} ry={2} />
          <text y={1} dominantBaseline="middle">{char.epithet}</text>
        </g>
      )}

      <text className="rg-name-zh" y={NAME_BLOCK_Y}>{char.display_name}</text>
      <text className="rg-name-en" y={NAME_BLOCK_Y + 16}>{char.name_en}</text>
      {dead && (
        <text className="rg-died" y={NAME_BLOCK_Y + 32}>已故</text>
      )}

      <Companion data={char.companion} />
    </g>
  );
}

// ─── Kin edge layer ─────────────────────────────────────────────────
function MarriageEdge({ a, b, label, sameRow, dimmed }) {
  const dimCls = dimmed ? ' is-dimmed' : '';
  if (sameRow) {
    // horizontal bar between two same-row portraits, "夫妻" pill mid-bar
    const y = a.y;
    const x1 = Math.min(a.x, b.x) + PORTRAIT_R;
    const x2 = Math.max(a.x, b.x) - PORTRAIT_R;
    const mid = (x1 + x2) / 2;
    return (
      <g className={`rg-kin rg-kin-marriage${dimCls}`}>
        <line x1={x1} y1={y} x2={x2} y2={y} />
        <PillLabel x={mid} y={y} text={label} kind="kin" />
      </g>
    );
  }
  // cross-row marriage (Daemon-Rhaenyra) — diagonal line, pill in middle
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  // start/end on the rim of each circle along the line direction
  const dx = b.x - a.x, dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / dist, uy = dy / dist;
  const x1 = a.x + ux * PORTRAIT_R, y1 = a.y + uy * PORTRAIT_R;
  const x2 = b.x - ux * PORTRAIT_R, y2 = b.y - uy * PORTRAIT_R;
  return (
    <g className={`rg-kin rg-kin-marriage rg-kin-cross-gen${dimCls}`}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      <PillLabel x={mx} y={my} text={label} kind="kin" />
    </g>
  );
}

function SiblingEdge({ a, b, label, dimmed }) {
  // Bracket *above* the two heads. Used only for siblings whose parents
  // aren't visible (would be redundant otherwise — already filtered out
  // server-side).
  const yBar = a.y + SIBLING_BAR_OFFSET;
  const xL = Math.min(a.x, b.x);
  const xR = Math.max(a.x, b.x);
  return (
    <g className={`rg-kin rg-kin-sibling${dimmed ? ' is-dimmed' : ''}`}>
      <line x1={a.x} y1={a.y - PORTRAIT_R} x2={a.x} y2={yBar} />
      <line x1={b.x} y1={b.y - PORTRAIT_R} x2={b.x} y2={yBar} />
      <line x1={xL} y1={yBar} x2={xR} y2={yBar} />
      <PillLabel x={(xL + xR) / 2} y={yBar} text={label} kind="kin" />
    </g>
  );
}

function ParentBracketEdge({ parents, children, positions, dimmed }) {
  // Skip if any participant is missing a position (defensive — shouldn't happen)
  for (const p of parents) if (!positions[p]) return null;
  for (const c of children) if (!positions[c]) return null;

  const parentPts = parents.map(p => positions[p]);
  // Anchor point: midpoint of the parent(s). For single parent, use their
  // own (x, y+r). For couple, midpoint of the marriage bar.
  let anchorX, anchorY;
  if (parents.length === 1) {
    anchorX = parentPts[0].x;
    anchorY = parentPts[0].y + PORTRAIT_R;
  } else {
    anchorX = (parentPts[0].x + parentPts[1].x) / 2;
    anchorY = parentPts[0].y;  // both same row; same-row marriage bar
  }

  const childPts = children.map(c => positions[c]);
  // Skip drawing if parent(s) and any child are in the same row (e.g.,
  // Otto→Alicent in our merged layout): would be a sideways edge that
  // looks broken. The user explicitly accepted this as an aesthetic
  // tradeoff for compactness.
  const allChildrenBelow = childPts.every(p => p.y > anchorY + 10);
  if (!allChildrenBelow) return null;

  const busY = anchorY + CHILD_BUS_OFFSET;
  const xs = childPts.map(p => p.x);
  const xL = Math.min(...xs);
  const xR = Math.max(...xs);

  return (
    <g className={`rg-kin rg-kin-parent${dimmed ? ' is-dimmed' : ''}`}>
      {/* trunk: vertical from parent anchor down to bus */}
      <line x1={anchorX} y1={anchorY} x2={anchorX} y2={busY} />
      {/* bus: horizontal bar spanning all kids' columns (extends to anchor if needed) */}
      <line x1={Math.min(xL, anchorX)} y1={busY} x2={Math.max(xR, anchorX)} y2={busY} />
      {/* legs: vertical drop from bus to each kid's top */}
      {childPts.map((p, i) => (
        <line key={i} x1={p.x} y1={busY} x2={p.x} y2={p.y - PORTRAIT_R} />
      ))}
      {/* "子女" pill on the trunk */}
      <PillLabel x={anchorX} y={(anchorY + busY) / 2} text="子女" kind="kin" />
    </g>
  );
}

// ─── Conflict overlay ───────────────────────────────────────────────
function ConflictEdge({ from, to, kind, relation, positions, future }) {
  const a = positions[from];
  const b = positions[to];
  if (!a || !b) return null;
  const dx = b.x - a.x, dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / dist, uy = dy / dist;
  const x1 = a.x + ux * PORTRAIT_R, y1 = a.y + uy * PORTRAIT_R;
  const x2 = b.x - ux * PORTRAIT_R, y2 = b.y - uy * PORTRAIT_R;
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const style = CONFLICT_STYLE[kind] || CONFLICT_STYLE.enemy;
  // future = the edge's triggering scene hasn't played yet. Click still
  // reveals the edge so the user can see "this character will be tied to
  // X later", but rendered with reduced opacity so it's clearly distinct
  // from edges that have already played.
  const cls = style.className + (future ? ' is-future' : '');
  return (
    <g className={cls}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      {relation && (
        <g className="rg-conf-label" transform={`translate(${mx}, ${my})`}>
          <rect x={-relation.length * 8 - 6} y={-11} width={relation.length * 16 + 12} height={22} rx={3} ry={3} />
          <text dominantBaseline="middle" textAnchor="middle">{relation}</text>
        </g>
      )}
    </g>
  );
}

// ─── Pan / zoom hook ────────────────────────────────────────────────
// Initial / reset scale: a 1.0×-ish "comfortable read" view rather than
// auto-fit-everything-in-drawer (which made portraits look like dots). The
// graph is wider than the drawer at this zoom — that's intended; pan and
// the time-driven focus pan reveal the rest.
const HOME_SCALE = 0.9;

function useViewport(graphSize, viewportRef, wheelSurfaceRef) {
  const [view, setView] = useState({ scale: HOME_SCALE, tx: 0, ty: 0 });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  // "Home" view: comfortable scale, centered on a target graph point (or
  // graph midpoint if none). Drives both the initial open and the ⟲ button.
  const fit = useCallback((targetX, targetY) => {
    const el = viewportRef.current;
    if (!el || !graphSize.width) return;
    const { clientWidth: vw, clientHeight: vh } = el;
    const cx = targetX != null ? targetX : graphSize.width / 2;
    const cy = targetY != null ? targetY : graphSize.height / 2;
    const scale = HOME_SCALE;
    setView({ scale, tx: vw / 2 - cx * scale, ty: vh / 2 - cy * scale });
  }, [graphSize.width, graphSize.height, viewportRef]);
  // No auto-fit-on-mount — the consumer decides when to home (typically on
  // first open, with the focused character's position so we land on whoever
  // the player is currently showing rather than the graph midpoint).

  // Pan-only: keep current scale, smoothly center the given graph point.
  // Used by the time-driven focus to bring the on-screen character into view.
  const centerOn = useCallback((gx, gy, targetScale) => {
    const el = viewportRef.current;
    if (!el || gx == null || gy == null) return;
    const vw = el.clientWidth, vh = el.clientHeight;
    setView(v => {
      const s = targetScale != null
        ? Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, targetScale))
        : v.scale;
      return { scale: s, tx: vw / 2 - gx * s, ty: vh / 2 - gy * s };
    });
  }, [viewportRef]);

  const onWheel = useCallback((e) => {
    if (!viewportRef.current) return;
    if (e.target instanceof Element && e.target.closest('.rg-profile')) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = viewportRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    setView(v => {
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v.scale * factor));
      const ratio = next / v.scale;
      return { scale: next, tx: mx - (mx - v.tx) * ratio, ty: my - (my - v.ty) * ratio };
    });
  }, [viewportRef]);

  useEffect(() => {
    const wheelSurface = wheelSurfaceRef.current;
    if (!wheelSurface) return undefined;
    wheelSurface.addEventListener('wheel', onWheel, { passive: false });
    return () => wheelSurface.removeEventListener('wheel', onWheel);
  }, [onWheel, wheelSurfaceRef]);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    dragRef.current = { x: e.clientX, y: e.clientY };
    setView(v => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
  }, []);

  const onMouseUp = useCallback(() => { dragRef.current = null; }, []);

  // touch: 1 finger = pan, 2 fingers = pinch zoom
  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      dragRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      pinchRef.current = null;
    } else if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinchRef.current = {
        d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        cx: (a.clientX + b.clientX) / 2,
        cy: (a.clientY + b.clientY) / 2,
      };
      dragRef.current = null;
    }
  }, []);

  const onTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const [a, b] = e.touches;
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const factor = d / pinchRef.current.d;
      pinchRef.current.d = d;
      const rect = viewportRef.current.getBoundingClientRect();
      const mx = pinchRef.current.cx - rect.left;
      const my = pinchRef.current.cy - rect.top;
      setView(v => {
        const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v.scale * factor));
        const ratio = next / v.scale;
        return { scale: next, tx: mx - (mx - v.tx) * ratio, ty: my - (my - v.ty) * ratio };
      });
    } else if (e.touches.length === 1 && dragRef.current) {
      const dx = e.touches[0].clientX - dragRef.current.x;
      const dy = e.touches[0].clientY - dragRef.current.y;
      dragRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setView(v => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
    }
  }, [viewportRef]);

  const onTouchEnd = useCallback(() => {
    dragRef.current = null;
    pinchRef.current = null;
  }, []);

  const zoomBy = useCallback((factor) => {
    setView(v => {
      const el = viewportRef.current;
      if (!el) return v;
      const cx = el.clientWidth / 2, cy = el.clientHeight / 2;
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v.scale * factor));
      const ratio = next / v.scale;
      return { scale: next, tx: cx - (cx - v.tx) * ratio, ty: cy - (cy - v.ty) * ratio };
    });
  }, [viewportRef]);

  return {
    view, fit, centerOn,
    onWheel, onMouseDown, onMouseMove, onMouseUp,
    onTouchStart, onTouchMove, onTouchEnd,
    zoomIn: () => zoomBy(1.25), zoomOut: () => zoomBy(1 / 1.25),
  };
}

// ─── Main component ─────────────────────────────────────────────────
// `embedded`: when true, the component renders as a tab body inside a
// parent overlay (StorylineXRay) — HUD button, scrim, cine-bars, top-right
// `×`, and ESC handler are all suppressed (the parent owns those). The
// component is effectively always-open; "close drawer" → no-op (the parent
// closes the X-ray container). Profile-panel and conflict-highlight
// click-outside fallbacks are PRESERVED in both modes (memory-pinned UX).
export default function RelationshipGraph({ videoId, videoRef, embedded = false, onCloseEmbedded }) {
  const [tree, setTree] = useState(null);
  const [focusList, setFocusList] = useState(null);
  const [charEvents, setCharEvents] = useState(null);   // per-video death/spawn timing
  const [profileMap, setProfileMap] = useState(null);   // pre-built static profiles keyed by character_id
  const [openState, setOpenState] = useState(false);
  const open = embedded ? true : openState;
  const closeDrawer = useCallback(() => {
    if (embedded) {
      // Parent (StorylineXRay) owns the close affordance; the third "peel"
      // layer is a no-op so blank-area click inside the graph doesn't yank
      // the user out of the X-ray view unexpectedly.
      onCloseEmbedded?.();
    } else {
      setOpenState(false);
    }
  }, [embedded, onCloseEmbedded]);
  const [hasNews, setHasNews] = useState(false);
  const [scanKey, setScanKey] = useState(0);
  const [error, setError] = useState(null);
  const [highlighted, setHighlighted] = useState(null);
  const [focused, setFocused] = useState(null);
  const [deadNow, setDeadNow] = useState(() => new Set());
  // charId → active version name; only populated for chars whose actor
  // changes mid-show (currently Rhaenyra/Alicent young↔adult). Falls back
  // to the family-tree default portrait when missing.
  const [versionsNow, setVersionsNow] = useState(() => new Map());
  // "from|to" keys for conflict edges whose triggering scene has already
  // played. Edges without a triggered_by_scene_id stay always-on and
  // aren't tracked here.
  const [activeEdges, setActiveEdges] = useState(() => new Set());
  const [profileId, setProfileId] = useState(null);
  const viewportRef = useRef(null);
  const wheelSurfaceRef = useRef(null);

  // Load an episode-specific tree first. Older episodes without a dedicated
  // snapshot keep using the original family-tree fallback.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTree(null);
    setError(null);
    const episodeTreeUrl = treeUrlForVideo(videoId);
    axios.get(freshSnapshotUrl(episodeTreeUrl), { timeout: 8000 })
      .catch(err => {
        if (episodeTreeUrl === FALLBACK_TREE_URL) throw err;
        return axios.get(freshSnapshotUrl(FALLBACK_TREE_URL), { timeout: 8000 });
      })
      .then(r => { if (!cancelled) setTree(r.data); })
      .catch(err => { if (!cancelled) setError(err?.message || String(err)); });
    return () => { cancelled = true; };
  }, [open, videoId]);

  // load pre-built static profiles for this video; fail-quiet if not present
  // (frontend will fall back to live /api/agent/character/profile per-click)
  useEffect(() => {
    if (!videoId) { setProfileMap(null); return; }
    let cancelled = false;
    const url = `${SNAPSHOT_BASE}/relationship-graph/_profiles-${encodeURIComponent(videoId.replace(/\.[^.]+$/, ''))}.json`;
    axios.get(freshSnapshotUrl(url), { timeout: 5000 })
      .then(r => { if (!cancelled) setProfileMap(r.data?.profiles || null); })
      .catch(() => { if (!cancelled) setProfileMap(null); });
    return () => { cancelled = true; };
  }, [videoId]);

  // load scene-focus map keyed on videoId; fail-quiet if not present
  useEffect(() => {
    if (!videoId) { setFocusList(null); return; }
    let cancelled = false;
    const url = `${SNAPSHOT_BASE}/relationship-graph/_scene-focus-${encodeURIComponent(videoId.replace(/\.[^.]+$/, ''))}.json`;
    axios.get(freshSnapshotUrl(url), { timeout: 5000 })
      .then(r => { if (!cancelled) setFocusList(r.data); })
      .catch(() => { if (!cancelled) setFocusList(null); });
    return () => { cancelled = true; };
  }, [videoId]);

  // load per-video character events ({character_id: {death_at}}). Without
  // it the family tree falls back to the static `char.alive` (final-state).
  useEffect(() => {
    if (!videoId) { setCharEvents(null); return; }
    let cancelled = false;
    const url = `${SNAPSHOT_BASE}/relationship-graph/_char-events-${encodeURIComponent(videoId.replace(/\.[^.]+$/, ''))}.json`;
    axios.get(freshSnapshotUrl(url), { timeout: 5000 })
      .then(r => { if (!cancelled) setCharEvents(r.data || null); })
      .catch(() => { if (!cancelled) setCharEvents(null); });
    return () => { cancelled = true; };
  }, [videoId]);

  useEffect(() => {
    if (!open) return;
    if (embedded) return;  // parent owns ESC in embedded mode
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Esc 与点击空白共用渐进关闭语义：profile → 高亮 → 抽屉。
      // closeOneLayer 在函数体下方定义，读取最新 state 时直接 inline 同款逻辑。
      if (profileId) { setProfileId(null); setHighlighted(null); return; }
      if (highlighted) { setHighlighted(null); return; }
      setOpenState(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, embedded, profileId, highlighted]);

  // reset state when video changes (in case the demo bumps to a different show)
  useEffect(() => { setHighlighted(null); setFocused(null); setProfileId(null); }, [videoId]);

  // close profile when the whole relationship graph drawer closes
  useEffect(() => { if (!open) setProfileId(null); }, [open]);

  // Time-driven polling: every 600 ms while the drawer is open, recompute
  // four time-derived states (focal char, deaths-now, active version per
  // char, visible-conflict edges) by comparing currentTime to the per-video
  // char-events map. Each state updates only when its content actually
  // changes, avoiding a full re-render of 18 portraits + 24 edges every
  // tick.
  useEffect(() => {
    if (!open || !videoRef?.current) return;
    if (!focusList && !charEvents) return;
    let timer = 0;
    let lastHero = null;
    let lastDeadKey = '';
    let lastVerKey = '';
    let lastEdgeKey = '';

    const versionSwaps = charEvents?.version_swaps || {};
    const deaths = charEvents?.deaths || {};
    const edgeAppears = charEvents?.edge_appears || {};

    // Pre-compute, for each char with version_swaps, the "initial" version
    // (the one not named in any swap entry). Falls back to the first key in
    // portraits_by_version if everything's named.
    const initialVersionByChar = {};
    if (tree) {
      for (const c of tree.characters) {
        const swaps = versionSwaps[c.character_id];
        if (!swaps) continue;
        const all = Object.keys(c.portraits_by_version || {});
        const swapped = new Set(Object.keys(swaps));
        initialVersionByChar[c.character_id] =
          all.find(v => !swapped.has(v)) || all[0] || c.actor_version || null;
      }
    }

    const tick = () => {
      const v = videoRef.current;
      if (v) {
        const t = v.currentTime;

        if (focusList) {
          let lo = 0, hi = focusList.length - 1, hit = null;
          while (lo <= hi) {
            const m = (lo + hi) >> 1;
            const r = focusList[m];
            if (t < r.start) hi = m - 1;
            else if (t >= r.end) lo = m + 1;
            else { hit = r; break; }
          }
          const heroId = hit ? hit.hero_id : null;
          if (heroId !== lastHero) {
            lastHero = heroId;
            setFocused(heroId);
          }
        }

        // deaths
        const deadSet = new Set();
        for (const id in deaths) {
          if (t >= deaths[id]) deadSet.add(id);
        }
        const deadKey = [...deadSet].sort().join(',');
        if (deadKey !== lastDeadKey) {
          lastDeadKey = deadKey;
          setDeadNow(deadSet);
        }

        // active version per char
        const versionMap = new Map();
        for (const charId in versionSwaps) {
          const swaps = versionSwaps[charId];
          let active = initialVersionByChar[charId];
          let bestT = -1;
          for (const ver in swaps) {
            const swapT = swaps[ver];
            if (t >= swapT && swapT > bestT) {
              bestT = swapT;
              active = ver;
            }
          }
          if (active) versionMap.set(charId, active);
        }
        const verKey = [...versionMap.entries()].map(([k, v]) => `${k}=${v}`).sort().join(',');
        if (verKey !== lastVerKey) {
          lastVerKey = verKey;
          setVersionsNow(versionMap);
        }

        // active conflict edges (scene-triggered ones)
        const edgeSet = new Set();
        for (const key in edgeAppears) {
          if (t >= edgeAppears[key]) edgeSet.add(key);
        }
        const edgeKey = [...edgeSet].sort().join('|');
        if (edgeKey !== lastEdgeKey) {
          lastEdgeKey = edgeKey;
          setActiveEdges(edgeSet);
        }
      }
      timer = window.setTimeout(tick, 600);
    };
    tick();
    return () => clearTimeout(timer);
  }, [open, focusList, charEvents, videoRef, tree]);

  const layout = useMemo(() => tree ? computeLayout(tree) : null, [tree]);
  const parentGroups = useMemo(
    () => tree ? groupParentChildren(tree.kin_edges) : [],
    [tree],
  );

  const conflictByChar = useMemo(() => {
    const m = new Map();
    if (!tree) return m;
    for (const e of tree.conflict_edges) {
      (m.get(e.from) || m.set(e.from, []).get(e.from)).push(e);
      (m.get(e.to) || m.set(e.to, []).get(e.to)).push(e);
    }
    return m;
  }, [tree]);

  // Per-character render state derived from currentTime (via deadNow /
  // versionsNow). Keeping this in one map avoids duplicating the lookup
  // inside the dim/highlight render loops below.
  const charRender = useMemo(() => {
    const m = new Map();
    if (!tree) return m;
    for (const c of tree.characters) {
      const hasTimedDeath = charEvents?.deaths?.[c.character_id] != null;
      const dead = hasTimedDeath ? deadNow.has(c.character_id) : !c.alive;
      const activeVer = versionsNow.get(c.character_id);
      const portraitUrl = (activeVer && c.portraits_by_version?.[activeVer])
        ? c.portraits_by_version[activeVer]
        : c.portrait_url;
      m.set(c.character_id, { dead, portraitUrl });
    }
    return m;
  }, [tree, charEvents, deadNow, versionsNow]);

  // True when an edge with a triggered_by_scene_id should render: either
  // its scene has played or there's no time gate at all.
  const edgeIsActive = useCallback((e) => {
    const key = `${e.from}|${e.to}`;
    if (charEvents?.edge_appears?.[key] == null) return true;
    return activeEdges.has(key);
  }, [charEvents, activeEdges]);

  const graphSize = layout ? { width: layout.width, height: layout.height } : { width: 0, height: 0 };
  const viewport = useViewport(graphSize, viewportRef, wheelSurfaceRef);

  const focusPos = focused && layout ? layout.positions[focused] : null;

  // useViewport returns a fresh wrapper object every render (because `view`
  // state changes whenever the user pans/zooms), so any effect depending on
  // `viewport` directly fires on every render — including those triggered by
  // the user's own drag/wheel — and snaps the view back. Stash a ref so the
  // effects below can reach `centerOn` / `fit` without putting the wrapper
  // in their dep arrays.
  const viewportFnsRef = useRef(viewport);
  viewportFnsRef.current = viewport;

  // First-open homing: once the drawer opens and the tree is laid out, jump
  // to the comfortable scale centered on the current focal character (or
  // graph midpoint if focus hasn't resolved yet). Gated by a ref so we don't
  // re-home when the user manually pans/zooms.
  const homedRef = useRef(false);
  useEffect(() => {
    if (!open) { homedRef.current = false; return; }
    if (homedRef.current || !graphSize.width) return;
    homedRef.current = true;
    const v = viewportFnsRef.current;
    if (focusPos) v.fit(focusPos.x, focusPos.y);
    else v.fit();
  }, [open, graphSize.width, focusPos]);

  // Time-driven focus change after the initial home: pan only (preserve the
  // user's current scale so manual zoom isn't wiped each scene).
  useEffect(() => {
    if (!open || !focusPos) return;
    if (!homedRef.current) return;        // first home will already cover this
    viewportFnsRef.current.centerOn(focusPos.x, focusPos.y);
  }, [focusPos, open]);

  // ⟲ button: re-home on the current focal character.
  const onResetView = useCallback(() => {
    if (focusPos) viewport.fit(focusPos.x, focusPos.y);
    else viewport.fit();
  }, [focusPos, viewport]);

  const openFocus = useCallback(() => {
    if (embedded) return;  // standalone-only entry point; parent controls open in embedded mode
    setOpenState(true);
    setHasNews(false);
    setScanKey(k => k + 1);
  }, [embedded]);

  const close = useCallback(() => closeDrawer(), [closeDrawer]);

  const onCharClick = useCallback((id) => {
    setHighlighted(prev => prev === id ? null : id);
    setProfileId(id);
  }, []);

  const closeProfile = useCallback(() => setProfileId(null), []);

  // 渐进关闭：profile → 冲突高亮 → 整个抽屉，一次只剥一层。Esc / 抽屉
  // 外部 scrim / 图内空白处 click 共用同一逻辑，跟键盘节奏对齐。
  // 注意：profile 关闭时不连带清 highlighted —— 用户反馈「点空白先关详情、再
  // 点一次才关剧情冲突线」。两层是独立的，必须分两次点击。
  const closeOneLayer = useCallback(() => {
    if (profileId) { setProfileId(null); return; }
    if (highlighted) { setHighlighted(null); return; }
    closeDrawer();
  }, [profileId, highlighted, closeDrawer]);

  // 图内点击：在 SVG 空白 / kin 边上 click（不在角色节点上）走 closeOneLayer。
  // closest('.rg-node') 确保点头像 clip-path 内部仍算点节点本身。
  const clearOverlays = useCallback((e) => {
    if (e.target?.closest && !e.target.closest('.rg-node')) {
      closeOneLayer();
    }
  }, [closeOneLayer]);

  // 不在 mousedown 阶段做剥离 —— 否则 click 触发时 profile/highlight 已经
  // 都被清了，剥层就跨了两层。剥离改在 click 走 closeOneLayer。代价：profile
  // 还开着时拖拽，panel 仍盖住右 40% 视野——用户可以先点空白关 panel 再拖。
  const onViewportMouseDown = useCallback((e) => {
    viewport.onMouseDown(e);
  }, [viewport]);

  const onViewportTouchStart = useCallback((e) => {
    viewport.onTouchStart(e);
  }, [viewport]);

  const activeConflicts = useMemo(
    () => highlighted ? (conflictByChar.get(highlighted) || []) : [],
    [highlighted, conflictByChar]
  );

  // When a character is highlighted, the conflict line between them and a
  // partner can pass behind unrelated portraits sitting in the middle. To
  // keep the line readable we dim every node that isn't involved in the
  // active conflicts, and re-render the involved nodes ABOVE the conflict
  // edges so the line is occluded only by the two endpoints.
  const involvedIds = useMemo(() => {
    if (!highlighted) return null;
    const set = new Set([highlighted]);
    // Click reveals every connection on this character — even ones whose
    // scene hasn't played yet. The future-vs-now distinction is conveyed
    // visually via ConflictEdge's `future` prop, not by hiding the edge.
    for (const e of activeConflicts) {
      set.add(e.from); set.add(e.to);
    }
    return set;
  }, [highlighted, activeConflicts]);

  return (
    <div className={`rg-root ${open ? 'is-viewing' : ''} ${embedded ? 'is-embedded' : ''}`}>
      {!embedded && <div className="rg-cine-bar" />}
      {!embedded && <div className="rg-cine-bar rg-cine-bar-bottom" />}

      {!embedded && (
        <div className="rg-hud-edge">
          <div
            className={`rg-hud-icon ${hasNews ? 'has-news' : ''}`}
            onClick={openFocus}
            title="人物关系图谱"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8.5" cy="8" r="2.6" />
              <path d="M3.6 18c.5-2.6 2.6-4.4 4.9-4.4s4.4 1.8 4.9 4.4" />
              <circle cx="16" cy="9.5" r="2.2" />
              <path d="M13.5 18.5c.4-2.1 2.1-3.6 4-3.6s3.6 1.5 4 3.6" />
              <line x1="9.5" y1="11" x2="14" y2="11" strokeDasharray="1 2" />
            </svg>
            <span className="rg-hud-icon-label">关系</span>
          </div>
        </div>
      )}

      <div className={`rg-focus-overlay ${open ? 'open' : ''}`}>
        {!embedded && <div className="rg-scrim" onClick={closeOneLayer} />}
        <div
          ref={wheelSurfaceRef}
          className="rg-tree-card"
          onClickCapture={(e) => {
            // 只有在 SVG viewport 空白处的点击才走"渐进剥离"
            // (profile → highlight → drawer)。header / legend / scan-line /
            // 卡片 padding 这些"装饰区"不再触发 —— 避免用户读 profile 时
            // 随手点周围把档案带飞。viewport 内部点 .rg-node 仍走节点本身的
            // click handler；这里只兜空白。embedded 模式下第三层是 noop。
            const t = e.target;
            if (!t || !t.closest) return;
            if (t.closest('.rg-profile')) return;
            if (t.closest('.rg-node')) return;
            if (t.closest('.rg-zoom-controls')) return;
            if (t.closest('.rg-close')) return;
            if (!t.closest('.rg-tree-viewport')) return;
            closeOneLayer();
          }}
        >
          <div className="rg-scan-line" key={scanKey} />
          {!embedded && <button className="rg-close" onClick={close} title="关闭 (Esc)">×</button>}

          <header className="rg-tree-header">
            <div className="rg-tree-title-zh">
              {tree?.title_zh}
              {tree?.subtitle_zh ? <span className="rg-tree-subtitle-zh">{tree.subtitle_zh}</span> : null}
            </div>
            <div className="rg-tree-title-en">
              <span>{tree?.title_en}</span>
              {tree?.subtitle_en ? <span className="rg-tree-subtitle-en">{tree.subtitle_en}</span> : null}
            </div>
          </header>

          <div className="rg-zoom-controls">
            <button onClick={viewport.zoomIn} title="放大">＋</button>
            <button onClick={viewport.zoomOut} title="缩小">−</button>
            <button onClick={onResetView} title="适配">⟲</button>
          </div>

          <div
            className="rg-tree-viewport"
            ref={viewportRef}
            onMouseDown={onViewportMouseDown}
            onMouseMove={viewport.onMouseMove}
            onMouseUp={viewport.onMouseUp}
            onMouseLeave={viewport.onMouseUp}
            onTouchStart={onViewportTouchStart}
            onTouchMove={viewport.onTouchMove}
            onTouchEnd={viewport.onTouchEnd}
            onDoubleClick={onResetView}
            onClick={clearOverlays}
          >
            {error && <div className="rg-error">关系图加载失败：{error}</div>}
            {tree && layout && (
              <svg
                className="rg-tree-svg"
                width={layout.width}
                height={layout.height}
                style={{
                  transform: `translate(${viewport.view.tx}px, ${viewport.view.ty}px) scale(${viewport.view.scale})`,
                  transformOrigin: '0 0',
                }}
              >
                {/* kin layer —— 当某角色被点中（highlighted）时，跟他无关的
                    亲缘 / 婚姻 / 兄弟 / 父子边变暗，让用户的视线集中在和这个
                    角色直接相连的边上。无 highlight 时全亮。 */}
                <g className="rg-kin-layer">
                  {parentGroups.map((g, i) => {
                    const involves = !highlighted
                      || g.parents.includes(highlighted)
                      || g.children.includes(highlighted);
                    return (
                      <ParentBracketEdge
                        key={`pg-${i}`}
                        parents={g.parents}
                        children={g.children}
                        positions={layout.positions}
                        dimmed={!involves}
                      />
                    );
                  })}
                  {tree.kin_edges
                    .filter(e => e.kind === 'marriage')
                    .map((e, i) => {
                      const a = layout.positions[e.from];
                      const b = layout.positions[e.to];
                      if (!a || !b) return null;
                      const involves = !highlighted || e.from === highlighted || e.to === highlighted;
                      return (
                        <MarriageEdge
                          key={`mar-${i}`}
                          a={a} b={b}
                          label={e.label}
                          sameRow={a.y === b.y}
                          dimmed={!involves}
                        />
                      );
                    })}
                  {tree.kin_edges
                    .filter(e => e.kind === 'sibling')
                    .map((e, i) => {
                      const a = layout.positions[e.from];
                      const b = layout.positions[e.to];
                      if (!a || !b) return null;
                      const involves = !highlighted || e.from === highlighted || e.to === highlighted;
                      return <SiblingEdge key={`sib-${i}`} a={a} b={b} label={e.label} dimmed={!involves} />;
                    })}
                </g>

                {/* Dimmed (bottom) node layer: characters NOT involved in
                    the active conflict. Drawn before the conflict edges so
                    the red line passes visibly OVER unrelated portraits. */}
                <g className="rg-node-layer rg-node-layer-dim">
                  {tree.characters
                    .filter(c => involvedIds && !involvedIds.has(c.character_id))
                    .map(c => {
                      const r = charRender.get(c.character_id) || {};
                      return (
                        <CharacterNode
                          key={c.character_id}
                          char={c}
                          position={layout.positions[c.character_id]}
                          highlighted={false}
                          focused={focused === c.character_id}
                          dead={r.dead}
                          portraitUrl={r.portraitUrl}
                          dimmed
                          onClick={onCharClick}
                        />
                      );
                    })}
                </g>

                {/* conflict overlay (only when a character is highlighted).
                    All edges render so the user actually sees the character
                    has connections — but edges whose triggering scene
                    hasn't played yet pass `future` and CSS dims them. */}
                {highlighted && (
                  <g className="rg-conflict-layer">
                    {activeConflicts.map((e, i) => (
                      <ConflictEdge
                        key={`conf-${i}`}
                        from={e.from} to={e.to}
                        kind={e.kind} relation={e.relation}
                        future={!edgeIsActive(e)}
                        positions={layout.positions}
                      />
                    ))}
                  </g>
                )}

                {/* Top node layer: highlighted character + their conflict
                    partners. Drawn AFTER the conflict edges so the red line
                    is only occluded by the actual endpoints. When nothing is
                    highlighted, this layer carries every character. */}
                <g className="rg-node-layer">
                  {tree.characters
                    .filter(c => !involvedIds || involvedIds.has(c.character_id))
                    .map(c => {
                      const r = charRender.get(c.character_id) || {};
                      return (
                        <CharacterNode
                          key={c.character_id}
                          char={c}
                          position={layout.positions[c.character_id]}
                          highlighted={highlighted === c.character_id}
                          focused={focused === c.character_id}
                          dead={r.dead}
                          portraitUrl={r.portraitUrl}
                          onClick={onCharClick}
                        />
                      );
                    })}
                </g>
              </svg>
            )}
          </div>

          <footer className="rg-tree-legend">
            <span className="rg-legend-hint">点击角色 → 显示该角色的剧情冲突线 + 人物档案</span>
          </footer>

          {/* 人物档案侧栏：点角色头像后从右侧浮入 */}
          <ProfilePanel
            character={tree?.characters?.find(c => c.character_id === profileId) || null}
            videoId={videoId}
            videoRef={videoRef}
            staticProfile={profileMap ? profileMap[profileId] : null}
            onClose={closeProfile}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Profile panel (slides in from the right edge of the tree card) ───
// Static fields (portrait, name, house, companion) come from the family-tree
// JSON immediately. The detailed analysis prefers the pre-built static
// profile snapshot (_profiles-<videoId>.json, generated by
// server/scripts/build-character-profiles.js) — that's instant and avoids
// an LLM round-trip. If the static snapshot is missing for this character,
// it falls back to the live /api/agent/character/profile endpoint, which
// fuses cursor-filtered KB + scene fact + house lore via the LLM. Either
// path is strictly spoiler-safe: only beats at-or-before the cursor surface.
function ProfilePanel({ character, videoId, videoRef, staticProfile, onClose }) {
  const open = !!character;
  const [profile, setProfile] = useState(null);   // { headline, analysis, arc_so_far, book_note }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqSeqRef = useRef(0);

  useEffect(() => {
    if (!character || !videoId) { setProfile(null); setError(null); return; }

    // Prefer the pre-built snapshot — instant, no network.
    if (staticProfile && staticProfile.analysis) {
      setProfile(staticProfile);
      setLoading(false);
      setError(null);
      return;
    }

    // Fallback: hit the live agent.
    const seq = ++reqSeqRef.current;
    setLoading(true);
    setError(null);
    setProfile(null);
    const t = videoRef?.current?.currentTime || 0;
    axios.post(`${API}/api/agent/character/profile`, {
      videoId,
      t,
      characterId: character.character_id,
    }, { timeout: 25000 })
      .then(r => {
        if (seq !== reqSeqRef.current) return;
        const d = r.data?.fallback || r.data;
        setProfile(d || null);
      })
      .catch(err => {
        if (seq !== reqSeqRef.current) return;
        const fallback = err?.response?.data?.fallback;
        if (fallback) setProfile(fallback);
        else setError(err?.message || String(err));
      })
      .finally(() => {
        if (seq === reqSeqRef.current) setLoading(false);
      });
  }, [character, videoId, videoRef, staticProfile]);

  return (
    <aside className={`rg-profile ${open ? 'is-open' : ''}`} aria-hidden={!open}>
      {character && (
        <>
          <button className="rg-profile-close" onClick={onClose} title="关闭档案">×</button>

          <div className="rg-profile-portrait">
            {character.portrait_url ? (
              <img src={character.portrait_url} alt={character.display_name} />
            ) : (
              <div className="rg-profile-portrait-fallback">
                {(character.display_name || '').slice(0, 1)}
              </div>
            )}
            {!character.alive && <div className="rg-profile-dead-band">已殁</div>}
          </div>

          <div className="rg-profile-name-zh">{character.display_name}</div>
          <div className="rg-profile-name-en">{character.name_en}</div>

          <div className="rg-profile-house">
            {character.epithet && <span className="rg-profile-epithet">{character.epithet}</span>}
            {character.house && <span className="rg-profile-house-tag">House {character.house}</span>}
          </div>

          {profile?.headline && (
            <div className="rg-profile-headline">{profile.headline}</div>
          )}

          {character.short_identity && (
            <div className="rg-profile-section">
              <div className="rg-profile-section-label">身份</div>
              <div className="rg-profile-section-body">{character.short_identity}</div>
            </div>
          )}

          {(loading || profile?.analysis || error) && (
            <div className="rg-profile-section">
              <div className="rg-profile-section-label">深度解读</div>
              {loading && <div className="rg-profile-skeleton" />}
              {!loading && profile?.analysis && (
                <div className="rg-profile-section-body rg-profile-analysis">{profile.analysis}</div>
              )}
              {!loading && !profile?.analysis && error && (
                <div className="rg-profile-error">解读暂时调不出：{error}</div>
              )}
            </div>
          )}

          {!loading && profile?.arc_so_far?.length > 0 && (
            <div className="rg-profile-section">
              <div className="rg-profile-section-label">至今的剧情节点</div>
              <ul className="rg-profile-arc">
                {profile.arc_so_far.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}

          {!loading && profile?.book_note && (
            <div className="rg-profile-section">
              <div className="rg-profile-section-label">血与火 · 背景</div>
              <div className="rg-profile-section-body rg-profile-book-note">{profile.book_note}</div>
            </div>
          )}

          {character.companion && (
            <div className="rg-profile-section">
              <div className="rg-profile-section-label">龙伴</div>
              <div className="rg-profile-companion">
                {character.companion.portrait_url && (
                  <img
                    className="rg-profile-companion-portrait"
                    src={character.companion.portrait_url}
                    alt={character.companion.display_name}
                  />
                )}
                <div>
                  <div className="rg-profile-companion-name">{character.companion.display_name}</div>
                  {character.companion.short_identity && (
                    <div className="rg-profile-companion-identity">{character.companion.short_identity}</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </aside>
  );
}
